
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Mail, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { toast } from "sonner";
import { useLanguage } from "@/components/LanguageContext";
import { Separator } from "@/components/ui/separator";

const loginSchema = z.object({
  email: z.string().email("Formato de email inválido"),
  password: z.string().min(6, "A senha deve ter pelo menos 6 caracteres")
});

type LoginFormProps = {
  onToggleForm: () => void;
  onForgotPassword?: () => void;
};

export const LoginForm = ({
  onToggleForm,
  onForgotPassword
}: LoginFormProps) => {
  const { language, t } = useLanguage();
  
  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: ""
    }
  });
  
  const onSubmit = async (values: z.infer<typeof loginSchema>) => {
    console.log("Tentando fazer login com:", values);
    try {
      const {
        data,
        error
      } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password
      });
      
      if (error) {
        console.error("Erro no login:", error);
        if (error.message === "Email not confirmed") {
          toast.error("Por favor, confirme seu email antes de fazer login. Verifique sua caixa de entrada.");
        } else if (error.message === "Invalid login credentials") {
          toast.error("Email ou senha incorretos");
        } else {
          toast.error("Erro ao fazer login. Tente novamente.");
        }
        return;
      }
      
      console.log("Login bem sucedido:", data);
      toast.success("Login realizado com sucesso!");
      
      // Remover redirecionamento manual - deixar que o useAuthRedirect gerencie
      // O AuthProvider e useAuthRedirect vão verificar o perfil e redirecionar adequadamente
    } catch (error: any) {
      console.error("Erro inesperado no login:", error);
      toast.error("Erro inesperado ao fazer login");
    }
  };
  
  return <div className="space-y-6">
      <div className="flex flex-col items-center space-y-4">
        <img src="/lovable-uploads/e79feb48-6e7c-488a-8626-bb79f4bde67a.png" alt="Logo" className="h-20 w-auto" />
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight dark:text-white">
            {t('auth.login.title')}
          </h1>
          <p className="text-sm text-muted-foreground dark:text-gray-400">{t('auth.login.subtitle')}</p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField control={form.control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel className="dark:text-gray-300">{t('auth.login.emailLabel')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input {...field} type="email" className="pl-10 dark:bg-gray-700 dark:border-gray-600 dark:text-white" placeholder={t('auth.login.emailPlaceholder')} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={form.control} name="password" render={({
          field
        }) => <FormItem>
                <FormLabel className="dark:text-gray-300">{t('auth.login.passwordLabel')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input {...field} type="password" className="pl-10 dark:bg-gray-700 dark:border-gray-600 dark:text-white" placeholder={t('auth.login.passwordPlaceholder')} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <Button type="submit" className="w-full">
            {t('auth.login.submitButton')}
          </Button>
        </form>
      </Form>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <Separator className="w-full" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            {language === 'pt' ? 'Ou continuar com' : 'Or continue with'}
          </span>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={async () => {
          const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
              redirectTo: window.location.origin,
            },
          });
          if (error) {
            console.error("Erro no login com Google:", error);
            toast.error(language === 'pt' ? 'Erro ao fazer login com Google' : 'Error signing in with Google');
          }
        }}
      >
        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
        Google
      </Button>

      <div className="text-center space-y-2">
        {onForgotPassword && (
          <button 
            onClick={onForgotPassword} 
            type="button"
            className="text-sm text-primary dark:text-white hover:underline"
          >
            {t('auth.login.forgotPassword')}
          </button>
        )}
        <div>
          <button onClick={onToggleForm} className="text-sm text-primary dark:text-white hover:underline">
            {t('auth.login.noAccount')}
          </button>
        </div>
      </div>
    </div>;
};
